import { TrendingUp, AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/ToastProvider'
import { useAiCashflow, useAiGenerateCashflow, useAiStatus } from '@/lib/api/hooks'
import type { AiCashflow } from '@/lib/api/types'

/** Cashflow forecast panel — az InvoicesPage tetejére tehető, vagy egy dashboardra. */
export function CashflowPanel({ companyId }: { companyId: string }) {
  const { data: aiState } = useAiStatus()
  const q = useAiCashflow(companyId)
  const gen = useAiGenerateCashflow()
  const toast = useToast()

  if (!aiState?.enabled) return null

  async function regen() {
    try {
      await gen.mutateAsync(companyId)
      toast.show({ tone: 'success', title: 'Cashflow összefoglaló kész' })
    } catch (err) {
      toast.show({ tone: 'error', title: 'Hiba', description: err instanceof Error ? err.message : 'Ismeretlen' })
    }
  }

  const cashflow = q.data?.result?.payload as unknown as AiCashflow | undefined

  return (
    <div className="rounded-md border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/20 px-3 py-2 text-sm mb-3">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-emerald-800 dark:text-emerald-200 flex items-center gap-1.5">
          <TrendingUp className="h-4 w-4" /> Cashflow (köv. 30 nap)
        </div>
        <Button size="sm" variant="ghost" onClick={regen} loading={gen.isPending} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>
          {cashflow ? 'Frissít' : 'Generálás'}
        </Button>
      </div>
      {q.isLoading ? (
        <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
          <Spinner size={12} /> Töltés…
        </div>
      ) : cashflow ? (
        <>
          <p className="text-sm text-slate-800 dark:text-slate-100 mt-1">{cashflow.narrative}</p>
          {cashflow.alerts.length > 0 && (
            <ul className="mt-2 space-y-1">
              {cashflow.alerts.map((a, i) => (
                <li
                  key={i}
                  className={`text-xs flex items-start gap-1.5 ${
                    a.severity === 'high'
                      ? 'text-red-800 dark:text-red-200'
                      : a.severity === 'warn'
                        ? 'text-amber-800 dark:text-amber-200'
                        : 'text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  {a.text}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p className="text-xs text-slate-500 mt-1">
          Még nem generáltunk forecast-ot. Kattints a <strong>Generálás</strong> gombra.
        </p>
      )}
    </div>
  )
}
